import { Button, theme } from "antd";
import { VizIcon } from "src/components/VizIcon";
import { SearchState } from "src/search/types";
import { ContentViews } from "../explore/lib/useContentView";

function VisualizationOption({
  title,
  value,
  disabled,
  selected,
  isDefault,
  onClick,
  isGrid,
}: {
  title: string;
  value: ContentViews;
  disabled: boolean;
  selected: boolean;
  isDefault: boolean;
  onClick: () => void;
  isGrid: boolean;
}) {
  if (isGrid) {
    return (
      <button
        onClick={disabled ? undefined : onClick}
        className={`viz-option ${selected ? "selected" : ""} ${
          disabled ? "disabled" : ""
        }`}
        disabled={disabled}
        type="button"
      >
        <VizIcon value={value} inactive={disabled} size={24} />
        <div className="viz-option-title">
          {title}
          {isDefault && <DefaultValue />}
        </div>
      </button>
    );
  }

  return (
    <Button
      block
      type="text"
      disabled={disabled}
      icon={<VizIcon value={value} inactive={disabled} />}
      onClick={disabled ? undefined : onClick}
      className={`viz-option-list ${selected ? "selected" : ""}`}
    >
      <span className="viz-option-title">{title}</span>
      {isDefault && <DefaultValue />}
    </Button>
  );
}

export function VisualizationMenu(props: {
  onClose(): void;
  onChange(view: ContentViews): void;
  default?: ContentViews;
  value?: ContentViews;
  state: SearchState;
  isGrid?: boolean;
}) {
  const isGrid = props.isGrid ?? true;

  const options = [
    {
      title: "Bar",
      value: ContentViews.barChart,
      disabled: !(
        arrayOf(props.state.groupBys, { min: 1 }) &&
        arrayOf(props.state.aggregations, { max: 1 })
      ),
    },
    {
      title: "Table",
      value: ContentViews.dataTable,
      disabled: false,
    },
    {
      title: "Heat Map",
      value: ContentViews.heatMap,
      disabled: !(
        arrayOf(props.state.groupBys, { min: 2, max: 2 }) &&
        arrayOf(props.state.aggregations, { max: 1 })
      ),
    },
    {
      title: "Metric",
      value: ContentViews.metric,
      disabled: !arrayOf(props.state.aggregations, { min: 1, max: 1 }),
    },
    {
      title: "Pivot",
      value: ContentViews.pivotTable,
      disabled: !(
        arrayOf(props.state.groupBys, { min: 2, max: 2 }) &&
        arrayOf(props.state.aggregations, { max: 1 })
      ),
    },
    {
      title: "Nested Table",
      value: ContentViews.nestedTable,
      disabled: !(
        arrayOf(props.state.groupBys, { min: 2, max: 3 }) &&
        arrayOf(props.state.aggregations, { max: 1 })
      ),
    },
    {
      title: "Time Series",
      value: ContentViews.overTime,
      disabled: false,
    },
    {
      title: "Tree Map",
      value: ContentViews.treeMap,
      disabled: !(
        arrayOf(props.state.groupBys, { min: 2, max: 2 }) &&
        arrayOf(props.state.aggregations, { max: 1 })
      ),
    },
  ];

  return (
    <div className="visualization-menu" style={{ border: "2px solid green" }}>
      <div
        className={isGrid ? "visualization-grid" : "visualization-list"}
        style={{ border: "2px solid yellow" }}
      >
        {options.map((option) => (
          <VisualizationOption
            key={option.value}
            title={option.title}
            value={option.value}
            disabled={option.disabled}
            selected={props.value === option.value}
            isDefault={props.default === option.value}
            onClick={() => {
              props.onChange(option.value);
            }}
            isGrid={isGrid}
          />
        ))}
      </div>
    </div>
  );
}

function arrayOf(
  arr: any[] | undefined,
  { min, max }: { min?: number; max?: number }
) {
  return (
    !!arr &&
    !(typeof min === "number" && arr.length < min) &&
    !(typeof max === "number" && arr.length > max)
  );
}

function DefaultValue() {
  const { token } = theme.useToken();
  return <span className="viz-option-default">(default)</span>;
}
